<template>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 lg:space-y-10">
        <!--Introduction-->
        <section class="bg-slate-50/60 rounded-2xl">
            <div class="w-full p-6 sm:p-8 lg:p-10">
                <div class="grid items-start gap-12 lg:grid-cols-2">
                    <!-- Left copy -->
                    <div>
                        <h6 class="text-blue-700 font-semibold tracking-wide uppercase text-xs">{{ content.intro.eyebrow }}</h6>
                        <h1 class="font-extrabold">{{ content.intro.title }}</h1>

                        <ul class="mt-8 space-y-4">
                            <FeatureItem v-for="(feature, index) in content.intro.features" :key="index" :title="feature.title" :desc="feature.desc" />
                        </ul>
                    </div>

                    <!-- Right pricing card -->
                    <div id="pricing-card">
                        <div class="overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-slate-200">
                            <!-- Header -->
                            <div class="flex flex-col items-center gap-3 border-b border-slate-100 px-8 py-8 text-center">
                                <img class="w-20 h-auto object-cover bg-slate-100 rounded-full" :src="productGroup.image" alt="Produktbild Asia+Titans" />
                                <div class="space-y-1">
                                    <h3 class="text-lg font-semibold">{{ content.pricing.title }}</h3>
                                    <p class="text-sm text-slate-500">{{ content.pricing.subtitle }}</p>
                                </div>
                            </div>

                            <!-- Price options -->
                            <ul v-for="productVariant in sortedProductVariants" :key="productVariant.id" class="divide-y divide-slate-100">
                                <PriceRow
                                    :label="$translate(getAttributeByType(productVariant, 'period').title)"
                                    :badge="productVariant.discount_percent * 100 === 10 ? 'Beliebt' : ''"
                                    :note="$t('per_month')"
                                    :sub="productVariant.discount_percent > 0 ? '-' + $toPercent(productVariant.discount_percent, 0) : ''"
                                    :price="$toCurrency(productVariant.base_price_gross - productVariant.base_price_gross * productVariant.discount_percent)"
                                    :class="selectedProductVariant === productVariant ? 'bg-blue-dark text-white' : ''"
                                    @click="$emit('select-product', productVariant)"
                                />
                            </ul>

                            <!-- CTA -->
                            <div class="px-6 pb-6 pt-4">
                                <button
                                    type="button"
                                    class="group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue px-5 py-3 text-sm font-semibold text-white shadow-md hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                                    @click="$emit('add-to-cart')"
                                >
                                    <span>{{ content.pricing.cta }}</span>
                                    <i class="fi fi-rs-arrow-right flex align-middle text-white" />
                                </button>

                                <p class="mt-3 text-center text-xs text-slate-500">
                                    {{ content.pricing.note }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!--So funktioniert's-->
        <section class="p-6 sm:p-8 lg:p-10 bg-gradient-to-b from-gray-100 to-gray-200 rounded-2xl shadow-inner">
            <div class="w-full">
                <!-- Header Block -->
                <div class="text-center mb-10">
                    <h2 class="text-2xl lg:text-3xl font-extrabold text-slate-900 leading-snug mb-6">{{ content.how.title }}</h2>
                    <p class="text-slate-600 max-w-4xl mx-auto text-sm lg:text-base whitespace-pre-line">{{ content.how.text }}</p>
                </div>

                <!-- Features Block -->
                <div>
                    <h3 class="mt-8 mb-8 text-xl font-bold text-center text-slate-800">{{ content.how.cardsTitle }}</h3>

                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                        <FeatureCard v-for="(card, index) in content.how.cards" :key="index" :title="card.title" :text="card.text" :icon-class="featureCardIcons[index]" />
                    </div>
                </div>
            </div>
        </section>

        <!--Aktien Liste-->
        <section class="w-full p-6 sm:p-8 lg:p-10">
            <div class="w-full">
                <h2 class="text-center mb-2">{{ content.stocksTitle }}</h2>
                <p class="mb-6 text-center text-sm text-slate-500">{{ content.stocksHint }}</p>
                <div class="grid lg:grid-cols-2 gap-8 justify-items-start">
                    <div v-for="gruppe in gruppierteAktien" :key="gruppe.category" class="mb-4 w-full">
                        <!-- Kategoriename -->
                        <h3 class="text-xl mb-2 text-center">
                            {{ gruppe.category }}
                        </h3>

                        <!-- Aktien pro Kategorie -->
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            <div
                                v-for="aktie in gruppe.aktien"
                                :key="aktie.title"
                                class="bg-blue text-white flex items-center px-3 py-1 rounded-lg w-full text-center justify-center lg:min-h-12 cursor-pointer"
                                @click="marketingModalOpen = aktie.title"
                            >
                                <span>{{ aktie.title }}</span>

                                <teleport to="body">
                                    <Popup type="text" :overflow-hidden="true" :open="marketingModalOpen === aktie.title" @open="closeAction()">
                                        <p class="pt-4">{{ aktie.description }}</p>
                                    </Popup>
                                </teleport>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!--Shopify-->
        <section
            v-if="productGroup.meta?.shopifyProductIds?.length > 0"
            class="lg:bg-gray-200 p-6 sm:p-8 lg:p-10 bg-gradient-to-b from-gray-100 to-gray-200 rounded-2xl shadow-inner"
        >
            <div class="w-full">
                <h2 class="text-center mb-4">Unsere Tassen</h2>
                <div class="gap-4 sm:gap-6 lg:gap-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 place-self-center justify-items-stretch">
                    <div v-for="shopifyProductId in productGroup.meta?.shopifyProductIds" :key="shopifyProductId" class="w-full h-full flex">
                        <ShopifyProduct :product-id="shopifyProductId" bg-color="bg-white" class="h-full" />
                    </div>
                </div>
            </div>
        </section>

        <!--Für wen ist das Paket geeignet-->
        <section class="p-6 sm:p-8 lg:p-10">
            <div class="w-full">
                <h2 class="text-center mb-8">{{ content.fit.title }}</h2>
                <ul class="max-w-none mx-auto space-y-4 text-slate-700 list-disc pl-6">
                    <li v-for="(item, index) in content.fit.items" :key="index">{{ item }}</li>
                </ul>
            </div>
        </section>

        <!--FAQ-->
        <section class="p-6 sm:p-8 lg:p-10">
            <div class="w-full">
                <strong data-aos="fade-right">
                    <h3 class="text-center mb-6">{{ content.faqTitle }}</h3>
                </strong>
                <div>
                    <div v-for="(faq, key) in faqsCurrent" :key="key" data-aos="fade-up">
                        <Accordeon :title="faq.question" :content="faq.answer" />
                    </div>
                </div>
            </div>
        </section>

        <!--Asia+Titans Analyse-->
        <section class="p-6 sm:p-8 lg:p-10 bg-gradient-to-b from-gray-100 to-gray-200 rounded-2xl shadow-inner">
            <div class="w-full grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <!-- Textbereich -->
                <div>
                    <h2 class="mb-4">{{ content.analysis.title }}</h2>
                    <p class="text-gray-600 mb-8 whitespace-pre-line">{{ content.analysis.text }}</p>

                    <div class="flex flex-col gap-6">
                        <FeatureItem v-for="(item, index) in content.analysis.items" :key="index" :title="item.title" :desc="item.desc" />
                    </div>
                </div>

                <!-- Bildbereich -->
                <div class="relative">
                    <div class="overflow-hidden rounded-2xl shadow-lg bg-gradient-to-b from-gray-800 to-gray-700 flex items-center justify-center h-56 sm:h-72 lg:h-80">
                        <img :src="$asset('/hkcm/images/unsereAnalyse.png')" alt="Chart Illustration" class="object-cover lg:object-fill w-full h-full bg-white" />
                    </div>
                </div>
            </div>
        </section>

        <!--Tech Aktien Erklärung-->
        <section>
            <div class="w-full p-6 sm:p-8 lg:p-10">
                <div>
                    <div class="w-full m-auto flex-col justify-center">
                        <h2>{{ content.explain.whatTitle }}</h2>
                        <p class="text-slate-600 whitespace-pre-line">{{ content.explain.whatText }}</p>
                    </div>
                </div>

                <div class="bg-gradient-to-b from-gray-100 to-gray-200 rounded-2xl shadow-inner p-8 lg:p-10 my-8">
                    <div class="w-full m-auto flex-col-reverse justify-center">
                        <h2>{{ content.explain.jpTitle }}</h2>
                        <p class="text-slate-600 whitespace-pre-line">{{ content.explain.jpText }}</p>
                    </div>
                </div>

                <div>
                    <div class="w-full m-auto flex-col justify-center">
                        <h2>{{ content.explain.opTitle }}</h2>
                        <p class="text-slate-600 whitespace-pre-line">{{ content.explain.opText }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!--Tech Aktien USA-->
        <section class="p-6 sm:p-8 lg:p-10 bg-gradient-to-b from-gray-100 to-gray-200 rounded-2xl shadow-inner text-left">
            <div class="w-full">
                <h2 class="text-center">{{ content.semi.title }}</h2>
                <p class="text-slate-600 whitespace-pre-line">{{ content.semi.text }}</p>
            </div>
        </section>

        <!--Wichtigkeit Tech Aktien-->
        <section class="bg-slate-50 rounded-2xl">
            <div class="w-full p-6 sm:p-8 lg:p-10">
                <div>
                    <p class="text-sm font-semibold tracking-wide text-blue-600 text-center lg:text-left mb-2">{{ content.market.label }}</p>
                    <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center lg:text-left mb-6">
                        {{ content.market.title }}
                    </h2>
                    <p class="text-slate-600 leading-relaxed text-sm lg:text-base lg:text-left whitespace-pre-line">{{ content.market.text }}</p>
                </div>
            </div>
        </section>

        <!--Floating Jetzt Paket buchen button -->
        <div
            :class="!pricingCardInView ? 'opacity-100' : 'opacity-0'"
            class="fixed bottom-4 left-0 right-0 mx-auto w-[calc(100%-1rem)] sm:w-fit z-50 transition duration-150 ease-in-out"
        >
            <a
                href="#pricing-card"
                class="bg-blue text-white px-4 sm:px-6 py-3 rounded-full shadow-lg flex items-center justify-center gap-2 sm:gap-3 hover:bg-blue-700 transition-colors ease-in-out"
            >
                <span class="text-sm sm:text-base leading-tight text-center">{{ content.floatingCta }}</span>
                <i class="fi fi-rs-shopping-cart text-white flex" />
            </a>
        </div>
    </div>
</template>

<script>
import legacyCompanies from '../../data/asiaTitans.json';
import companiesContent from '../../data/asiaTitansCompanies.i18n.json';
import pageContent from '../../data/asiaTitansPage.i18n.json';
import Accordeon from '../miscellaneous/Accordeon.vue';
import Popup from '../miscellaneous/Popup.vue';
import ShopifyProduct from '../miscellaneous/ShopifyProducts.vue';
import FeatureCard from './FeatureCard.vue';
import FeatureItem from './FeatureItem.vue';
import PriceRow from './PriceRow.vue';

export default {
    name: 'Tech33AktienPaket',
    components: {
        FeatureItem,
        PriceRow,
        FeatureCard,
        ShopifyProduct,
        Accordeon,
        Popup
    },
    props: {
        productGroup: {
            type: Object,
            required: true
        },
        selectedProductVariant: {
            type: Object,
            default: null
        },
        sortedProductVariants: {
            type: Array,
            default: () => []
        }
    },
    emits: ['select-product', 'add-to-cart'],
    data: () => ({
        pricingCardInView: false,
        marketingModalOpen: null,
        pricingCardObserver: null
    }),
    computed: {
        region() {
            return window.meta?.region_configuration?.region || 'en';
        },
        normalizedRegion() {
            return String(this.region || 'en')
                .toLowerCase()
                .trim();
        },
        languageKey() {
            return this.normalizedRegion === 'de' ? 'de' : 'en';
        },
        content() {
            return pageContent[this.languageKey] || pageContent.en || pageContent.de;
        },
        companies() {
            const byLang = companiesContent[this.languageKey];

            if (byLang?.info?.length) {
                return byLang;
            }

            if (companiesContent.en?.info?.length) {
                return companiesContent.en;
            }

            if (companiesContent.de?.info?.length) {
                return companiesContent.de;
            }

            return legacyCompanies;
        },
        faqsCurrent() {
            return this.content.faqs || [];
        },
        featureCardIcons() {
            return [
                'fi fi-rr-envelope-open-text',
                'fi fi-rr-chart-line-up',
                'fi fi-rr-bullseye',
                'fi fi-rr-bolt',
                'fi fi-rr-medal',
                'fi fi-rr-eye',
                'fi fi-rr-check-circle',
                'fi fi-rr-briefcase'
            ];
        },
        gruppierteAktien() {
            const groups = {};
            (this.companies.info || []).forEach((aktie) => {
                if (!groups[aktie.category]) {
                    groups[aktie.category] = [];
                }

                groups[aktie.category].push(aktie);
            });

            return Object.entries(groups).map(([category, aktien]) => ({
                category,
                aktien
            }));
        }
    },
    mounted() {
        this.setupPricingObserver();
    },
    beforeUnmount() {
        if (this.pricingCardObserver) {
            this.pricingCardObserver.disconnect();
            this.pricingCardObserver = null;
        }
    },
    methods: {
        getAttributeByType(productVariant, type) {
            return productVariant.productAttributeValues.data.find((productAttributeValue) => {
                return productAttributeValue.meta.type === type;
            });
        },
        closeAction() {
            this.marketingModalOpen = null;
        },
        setupPricingObserver() {
            const el = document.getElementById('pricing-card');

            if (el) {
                this.pricingCardObserver = new IntersectionObserver(
                    (entries) => {
                        const entry = entries[0];
                        this.pricingCardInView = entry.isIntersecting;
                    },
                    { root: null, threshold: 0.1 }
                );
                this.pricingCardObserver.observe(el);
            } else {
                this.$nextTick(() => {
                    const el2 = document.getElementById('pricing-card');

                    if (el2) {
                        this.pricingCardObserver = new IntersectionObserver(
                            (entries) => {
                                this.pricingCardInView = entries[0].isIntersecting;
                            },
                            { root: null, threshold: 0.1 }
                        );
                        this.pricingCardObserver.observe(el2);
                    }
                });
            }
        }
    }
};
</script>
